/**
 * Modbus helper functions.
 */

import { CoreTreeNodeBarControlPathQuery } from '@/features/core/components/tree-node-bar-control/__generated__/CoreTreeNodeBarControlPathQuery';
import { HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items } from '@/hsc-api/queries/__generated__/HeatingSystemCollectionWithPaginationQuery';
import { SeverityColor } from './heating-systems';
import { SpotRole } from '@/types/iot-portal';

export type HeatingSystem = HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items;

export type TreeNode = CoreTreeNodeBarControlPathQuery['treeNodes']['items'][number]['children']['items'][number] & {
  isOpen?: boolean;
};

export interface DecoderRegister {
  registerNumber: string;
  registerName: string;
  registerFormat: string;
  metricName: string;
  unit: string;
  dataType: string;
  offset: number;
  scaleFactor: number;
  functionCode: number;
  intervalSeconds: number;
  tableData: string[] | null;
  standardMetric: boolean;
}

export interface Decoder {
  deviceManufacturer: string;
  deviceModel: string;
  deviceRole: string;
  deviceVersion: string;
  registers: DecoderRegister[];
}

export interface ComponentGroup {
  groupName: string;
  type: string;
  spotId: string;
}

export function getComponentsGroupNames(heatingSystems: HeatingSystem[]): ComponentGroup[] {
  const components: ComponentGroup[] = [];

  heatingSystems.forEach((hs) => {
    hs.heatingSystemMeasurementGroups.forEach((group) => {
      group.heatingSystemMeasurements.forEach((measurement) => {
        const exists = components.find((item) => item.groupName === group.groupName);
        if (!exists) {
          components.push({
            groupName: group.groupName,
            type: group.systemComponentTypeName,
            spotId: measurement.spotId,
          });
        }
      });
    });
  });

  return components.sort((a, b) => {
    // ensure HEATING_CONTROL items are at the bottom
    if (a.type === 'HEATING_CONTROL') return 1;
    if (b.type === 'HEATING_CONTROL') return -1;

    return a.groupName.localeCompare(b.groupName);
  });
}

// this is a different severity mapping than the old/existing eneriq messages done in heating sysytems tab
export function getEnerIQSeverityColor(severity: number): SeverityColor {
  if (severity === -1) return SeverityColor.GRAY;
  if (severity === 0) return SeverityColor.RED;
  if (severity > 0 && severity <= 70) return SeverityColor.YELLOW;
  if (severity > 70 && severity <= 99) return SeverityColor.GREEN;
  if (severity === 100) return SeverityColor.GREEN;
  return SeverityColor.GRAY;
}

export const isDummyMetricValue = (metricValue: number): boolean => {
  return (
    Number.isNaN(metricValue) ||
    metricValue >= 32767 ||
    metricValue <= -32767 ||
    (metricValue >= 3276.6 && metricValue < 3277) ||
    (metricValue <= -3276.6 && metricValue > -3277)
  );
};

export const formatMetricValue = (value: string | number): string => {
  const numericValue = Number(value);
  if (isDummyMetricValue(numericValue)) {
    return '-';
  }
  return new Intl.NumberFormat('de-DE').format(Number(numericValue.toFixed(2)));
};

export const isHeatingControl = (spotRole: SpotRole | undefined): boolean => {
  return spotRole ? spotRole === SpotRole.HEATING_CONTROL : false;
};
